import dayjs, { Dayjs } from 'dayjs';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import {
  clearModal,
  getFullCohortDslForStudyPeriodRestriction,
  selectBaselinePeriodRestrictionDaysBeforeIndexDate,
  selectBaselinePeriodRestrictionIsEnabled,
  selectCohortDsl,
  selectFollowUpPeriodRestrictionDaysAfterIndexDate,
  selectFollowUpPeriodRestrictionIsEnabled,
  selectIndexDate,
  selectOverallStudyPeriodRestrictionEndDate,
  selectOverallStudyPeriodRestrictionIsEnabled,
  selectOverallStudyPeriodRestrictionStartDate,
  setStudyPeriodRestrictions,
  useCohortEditable,
} from '@verantos-dev/app-state-cohort-builder';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { ApiWorkflowDialog } from '@verantos-dev/shared-components';
import { CohortStatusLegacy } from '@verantos-dev/types-cohorts';

import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useResearchAssistantUpdateForStudyPeriodCard';
import { BaselinePeriodRestriction } from '../../cohort-builder-sections/StudyPeriodSection/BaselinePeriodRestriction';
import { FollowUpPeriodRestriction } from '../../cohort-builder-sections/StudyPeriodSection/FollowUpPeriodRestriction';
import { StudyPeriodRestriction } from '../../cohort-builder-sections/StudyPeriodSection/StudyPeriodRestriction';

export const StudyPeriodEditCard = () => {
  const dialogRef = useRef(null);
  const dispatch = useAppDispatch();
  const intl = useIntl();

  /** Cohort Access Level */
  const { data: cohortData } = useCohortBuilderData();
  const isProcessing =
    !!cohortData?.data.latestStatus &&
    [CohortStatusLegacy.PROCESSING, CohortStatusLegacy.FINAL].includes(
      cohortData?.data.latestStatus
    );
  const { isEditable } = useCohortEditable();
  const viewMode = !isEditable || isProcessing;

  /** Research Assistant Sync */
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate(viewMode);

  /** Cohort Definition State - gets initial state*/
  const cohortDSL = useAppSelector(selectCohortDsl);
  const _isStudyPeriodRestrictionEnabled = useAppSelector(
    selectOverallStudyPeriodRestrictionIsEnabled
  );
  const _studyPeriodRestrictionStartDate = useAppSelector(
    selectOverallStudyPeriodRestrictionStartDate
  );
  const _studyPeriodRestrictionEndDate = useAppSelector(selectOverallStudyPeriodRestrictionEndDate);
  const [hasDateBoundsError, setHasDateBoundsError] = useState<boolean>(false);

  const _isBaselinePeriodRestrictionEnabled = useAppSelector(
    selectBaselinePeriodRestrictionIsEnabled
  );
  const _daysBeforeIndexDate = useAppSelector(selectBaselinePeriodRestrictionDaysBeforeIndexDate);

  const _isFollowUpPeriodRestrictionEnabled = useAppSelector(
    selectFollowUpPeriodRestrictionIsEnabled
  );
  const _daysAfterIndexDate = useAppSelector(selectFollowUpPeriodRestrictionDaysAfterIndexDate);

  const hasIndexDate = !!useAppSelector(selectIndexDate);

  /** Initialize local state */
  const [isStudyPeriodRestrictionEnabled, setIsStudyPeriodRestrictionEnabled] = useState<boolean>(
    !!_isStudyPeriodRestrictionEnabled
  );
  const [studyPeriodStartDate, setStudyPeriodStartDate] = useState<Dayjs | null>(
    _studyPeriodRestrictionStartDate ? dayjs.utc(new Date(_studyPeriodRestrictionStartDate)) : null
  );
  const [studyPeriodEndDate, setStudyPeriodEndDate] = useState<Dayjs | null>(
    _studyPeriodRestrictionEndDate ? dayjs.utc(new Date(_studyPeriodRestrictionEndDate)) : null
  );

  const [isBaselinePeriodRestrictionEnabled, setIsBaselinePeriodRestrictionEnabled] =
    useState<boolean>(!!_isBaselinePeriodRestrictionEnabled);
  const [baselinePeriodDaysBeforeIndexDate, setBaselinePeriodDaysBeforeIndexDate] = useState<
    number | undefined
  >(_daysBeforeIndexDate);

  const [isFollowUpPeriodRestrictionEnabled, setIsFollowUpPeriodRestrictionEnabled] =
    useState<boolean>(!!_isFollowUpPeriodRestrictionEnabled);
  const [followUpPeriodDaysAfterIndexDate, setFollowUpPeriodDaysAfterIndexDate] = useState<
    number | undefined
  >(_daysAfterIndexDate);

  /** Compute error states */
  const hasStudyPeriodError =
    isStudyPeriodRestrictionEnabled && (!studyPeriodStartDate || !studyPeriodEndDate);
  const hasBaselinePeriodError =
    isBaselinePeriodRestrictionEnabled && baselinePeriodDaysBeforeIndexDate === undefined;
  const hasFollowUpPeriodError =
    isFollowUpPeriodRestrictionEnabled && followUpPeriodDaysAfterIndexDate === undefined;

  const hasError =
    hasStudyPeriodError || hasDateBoundsError || hasBaselinePeriodError || hasFollowUpPeriodError;

  const onClose = () => {
    dispatch(clearModal());
  };

  const onSaveButtonClick = () => {
    const _nextStudyPeriodRestrictions = {
      overallStudyPeriodRestriction: isStudyPeriodRestrictionEnabled
        ? {
            isEnabled: isStudyPeriodRestrictionEnabled,
            startDate: studyPeriodStartDate?.toISOString() || '',
            endDate: studyPeriodEndDate?.toISOString() || '',
          }
        : undefined,
      baselinePeriodRestriction: {
        isEnabled: isBaselinePeriodRestrictionEnabled,
        daysBeforeIndexDate: isBaselinePeriodRestrictionEnabled
          ? baselinePeriodDaysBeforeIndexDate
          : undefined,
      },
      followUpPeriodRestriction: {
        isEnabled: isFollowUpPeriodRestrictionEnabled,
        daysAfterIndexDate: isFollowUpPeriodRestrictionEnabled
          ? followUpPeriodDaysAfterIndexDate
          : undefined,
      },
    };

    !viewMode && dispatch(setStudyPeriodRestrictions(_nextStudyPeriodRestrictions));

    /** handle research assistant */
    const nextCohortDsl = getFullCohortDslForStudyPeriodRestriction(
      _nextStudyPeriodRestrictions,
      cohortDSL
    );
    emitResearchAssistantUpdate(nextCohortDsl);
  };

  return (
    <ApiWorkflowDialog
      id="study-period-edit-card"
      title={intl.formatMessage({
        id: 'projects.cohortBuilderPage.studyPeriodSection.editCardTitle',
      })}
      size="medium"
      // isInitialLoading={isLoading}
      // initialLoadingError={conceptGroupsError}
      // onRetryInitialLoadingFetch={refetchConceptGroups}
      isValidForSubmit={!hasError && isEditable}
      isSubmitting={false}
      ref={dialogRef}
      onlyShowCloseButton={viewMode}
      onClose={onClose}
      onSubmit={onSaveButtonClick}
      // onSubmit={onSubmit}
      // isSubmitting={isSaveStudyArmLoading}
      // submittingError={saveStudyArmError}
    >
      <StudyPeriodRestriction
        viewMode={viewMode}
        setHasDateBoundsError={setHasDateBoundsError}
        isStudyPeriodRestrictionEnabled={isStudyPeriodRestrictionEnabled}
        setIsStudyPeriodRestrictionEnabled={setIsStudyPeriodRestrictionEnabled}
        studyPeriodStartDate={studyPeriodStartDate}
        setStudyPeriodStartDate={setStudyPeriodStartDate}
        studyPeriodEndDate={studyPeriodEndDate}
        setStudyPeriodEndDate={setStudyPeriodEndDate}
      />
      <BaselinePeriodRestriction
        viewMode={viewMode || !hasIndexDate}
        hasIndexDate={hasIndexDate}
        isBaselinePeriodRestrictionEnabled={isBaselinePeriodRestrictionEnabled}
        setIsBaselinePeriodRestrictionEnabled={setIsBaselinePeriodRestrictionEnabled}
        baselinePeriodDaysBeforeIndexDate={baselinePeriodDaysBeforeIndexDate}
        setBaselinePeriodDaysBeforeIndexDate={setBaselinePeriodDaysBeforeIndexDate}
      />
      <FollowUpPeriodRestriction
        viewMode={viewMode || !hasIndexDate}
        hasIndexDate={hasIndexDate}
        isFollowUpPeriodRestrictionEnabled={isFollowUpPeriodRestrictionEnabled}
        setIsFollowUpPeriodRestrictionEnabled={setIsFollowUpPeriodRestrictionEnabled}
        followUpPeriodDaysAfterIndexDate={followUpPeriodDaysAfterIndexDate}
        setFollowUpPeriodDaysAfterIndexDate={setFollowUpPeriodDaysAfterIndexDate}
      />
    </ApiWorkflowDialog>
  );
};
